import objectHash from "object-hash";
import React, { ReactElement, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import NamespaceRoleGuard from "../../../components/NamespaceRoleGuard";
import SaveBackdrop from "../../../components/SaveBackdrop";
import SaveButton from "../../../components/SaveButton";
import { useSelector } from "../../../store";
import {
  KeyPair,
  useCreateKeyPairMutation,
  useUpdateKeyPairMutation,
} from "../../../store/pomeriumApi";

// eslint-disable-next-line react-refresh/only-export-components
export const StyledTextField = styled(TextField)({
  "& div.MuiFilledInput-root": {
    background: `rgba(110, 67, 232, 0.05)`,
    padding: `0px`,
    marginTop: `10px`,
    display: `flex`,
    flexFlow: `row nowrap`,
    boxShadow: `0 0 0 1px rgb(63 63 68 / 5%), 0 1px 2px 0 rgb(63 63 68 / 15%)`,

    "& div.MuiFilledInput-root": {
      margin: `2px 0px 0px 6px`,
      height: `100%`,
    },
    "& input.MuiInputBase-input": {
      padding: `6px`,
      margin: `6px`,
    },

    "& .MuiFilledInput-inputMultiline": {
      padding: `10px`,
    },
  },
  "& div.MuiFilledInput-underline:before": {
    borderBottom: `0px`,
  },
  "& div.MuiFilledInput-underline:after": {
    border: `0px`,
  },
});

interface AddFormProps {
  open: boolean;
  onClose: () => void;
  keyPairId?: string;
  certs: KeyPair[];
}

const AddFormDialog = ({
  open,
  onClose,
  keyPairId,
  certs,
}: AddFormProps): ReactElement => {
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [certText, setCertText] = useState("");
  const [keyText, setKeyText] = useState("");
  const [nameText, setNameText] = useState("");
  const certRef = React.useRef(null);
  const keyRef = React.useRef(null);
  const [createCert, { isLoading: creating }] = useCreateKeyPairMutation();
  const [updateCert, { isLoading: updating }] = useUpdateKeyPairMutation();

  const handleCreate = async (): Promise<void> => {
    const res = await createCert({
      organizationId,
      keyPairWithKeyProperties: {
        namespaceId: clusterId,
        certificate: certText,
        key: keyText,
        name: nameText,
      },
    });
    if (!("error" in res)) {
      clearLocalState();
      onClose();
    }
  };

  const handleUpdate = async (): Promise<void> => {
    const res = await updateCert({
      organizationId,
      keyPairId: keyPairId as string,
      keyPairWithKeyProperties: {
        namespaceId: clusterId,
        certificate: certText,
        key: keyText,
        name: nameText,
      },
    });
    if (!("error" in res)) {
      clearLocalState();
      onClose();
    }
  };

  const handleSave = keyPairId ? handleUpdate : handleCreate;

  const handleCertFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!evt.target.files) return;
    const file = evt.target.files[0];
    const reader = new FileReader();
    reader.onload = function (e) {
      setCertText(e?.target?.result as string);
    };
    reader.readAsText(file);
  };

  const handleKeyFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!evt.target.files) return;
    const file = evt.target.files[0];
    const reader = new FileReader();
    reader.onload = function (e) {
      setKeyText(e?.target?.result as string);
    };
    reader.readAsText(file);
  };

  const clearLocalState = (): void => {
    setCertText("");
    setKeyText("");
    setNameText("");
  };

  useEffect(() => {
    const originalCert = certs.find((c) => c.id === keyPairId);
    if (originalCert && open) {
      setCertText(originalCert?.certificate || "");
      setNameText(originalCert?.name || "");
    }
  }, [keyPairId, objectHash(certs), open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        {keyPairId ? "Update Certificate" : "Add Certificate"}
        <IconButton
          color="primary"
          onClick={() => {
            onClose();
            clearLocalState();
          }}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <NamespaceRoleGuard
          namespaceId={clusterId}
          role={"manager"}
          warn={true}
        >
          <form onSubmit={handleSave}>
            <SaveBackdrop saving={creating || updating} />
            <Container disableGutters>
              <Grid container spacing={2} alignItems="stretch">
                <Grid size={12}>
                  <Typography variant="body2">Name</Typography>
                  <StyledTextField
                    fullWidth
                    variant="filled"
                    value={nameText}
                    onChange={(evt): void => setNameText(evt.target.value)}
                    spellCheck={false}
                  />
                </Grid>
                <Grid size={12}>
                  <input
                    style={{ display: "none" }}
                    id="cert-file"
                    ref={certRef}
                    type="file"
                    onChange={handleCertFile}
                  />
                  <label htmlFor="cert-file">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                    >
                      Certificate from file
                    </Button>
                  </label>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2">Certificate text</Typography>
                  <StyledTextField
                    fullWidth
                    variant="filled"
                    value={certText}
                    multiline={true}
                    rows={5}
                    placeholder={"e.g. copy/paste the cert in PEM format"}
                    onChange={(evt): void => setCertText(evt.target.value)}
                    spellCheck={false}
                  />
                </Grid>
                <Grid size={12}>
                  <input
                    style={{ display: "none" }}
                    id="key-file"
                    ref={keyRef}
                    type="file"
                    onChange={handleKeyFile}
                  />
                  <label htmlFor="key-file">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                    >
                      Certificate key from file
                    </Button>
                  </label>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2">
                    Certificate key text (optional)
                  </Typography>
                  <StyledTextField
                    fullWidth
                    variant="filled"
                    value={keyText}
                    multiline={true}
                    rows={5}
                    placeholder={"e.g. copy/paste the key in PEM format"}
                    onChange={(evt): void => setKeyText(evt.target.value)}
                  />
                </Grid>
              </Grid>
            </Container>
          </form>
        </NamespaceRoleGuard>
      </DialogContent>
      <DialogActions>
        <SaveButton
          disabled={!nameText && !certText && !keyText}
          variant="contained"
          color="primary"
          fullWidth={false}
          onClick={handleSave}
        >
          Submit
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};
export default AddFormDialog;
